import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { LOG_SERVICE, LogService } from "@cervval.js/common/src/services/log.service";
import sanitize from "sanitize-html";
import { FieldMarshaller, StringFieldConstraints } from "../sql.common";

export interface StringMarshallerOptions {

    /**
     * If false, the string will not be sanitized (before setting this to false, ask for advice...)
     * @default true
     */
    sanitizeHtml?: boolean;

    /**
     * Default value - {@link StringMarshaller.getStanitizerHtmlStrictOptions}
     */
    sanitizerHtmlOptions?: sanitize.IOptions;
    maxLength?: number;
    replaceNullWithEmptyString?: boolean;
    replaceEmptyStringWithNull?: boolean;
}

export class StringMarshaller implements FieldMarshaller<string, string> {

    @Injected(LOG_SERVICE)
    public readonly logService!: LogService;

    /** Option object for sanitize html */
    public sanitizerHtmlOptions: sanitize.IOptions;

    constructor(protected _options: StringMarshallerOptions = {}) {

        // SanitizeHtml default true
        this._options.sanitizeHtml ??= true;

        if (this._options.sanitizerHtmlOptions) {
            this.sanitizerHtmlOptions = this._options.sanitizerHtmlOptions;
        } else {
            this.sanitizerHtmlOptions = StringMarshaller.getStanitizerHtmlStrictOptions();
        }
    }

    /**
     * convert json to db data
     */
    public marshal(obj: string | null | undefined): string | null | undefined {

        if (obj == null) {
            // replace null by empty string 
            return this._options.replaceNullWithEmptyString ? "" : obj;
        }

        if (obj === "" && this._options.replaceEmptyStringWithNull) {
            // replace empty string by null
            return null;
        }

        // Keep only {maxLength} caracters if given
        if (this._options.maxLength && obj.length > this._options.maxLength) {
            obj = obj.substr(0, this._options.maxLength);
        }

        // Sanitize html
        if (this._options.sanitizeHtml) {

            // Sanitize potentially harmfull HTML to harmless XSS free HTML.
            obj = sanitize(obj, this.sanitizerHtmlOptions);
        }

        return obj;
    }

    /** @returns default html strict options for sanitize string */
    public static getStanitizerHtmlStrictOptions(): sanitize.IOptions {
        return {
            allowedTags: ["h3", "h4", "h5", "h6", "blockquote", "p", "a", "ul", "ol",
                "nl", "li", "b", "i", "strong", "em", "strike", "code", "hr", "br", "div",
                "table", "thead", "caption", "tbody", "tr", "th", "td", "pre", "span"],
            allowedAttributes: {
                a: ["href", "name", "target"],
                img: ["src"],
                span: ["style"],
                p: ["style"],
                ul: ["style"],
                ol: ["style"]
            },
            allowedStyles: {
                "*": {
                    // color : hex or rgb
                    color: [/^#(0x)?[0-9a-f]+$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/],
                    "text-align": [/^left$/, /^right$/, /^center$/, /^justify$/],
                    // font-size in px, pt, em or %
                    "font-size": [/^\d+(?:px|em|%|pt)$/],
                    // list style from TinyMCE
                    "list-style-type": [/^circle$/, /^disc$/, /^square$/, /^lower-alpha$/, /^lower-greek$/, /^lower-roman$/, /^upper-alpha$/, /^upper-roman$/],
                    // indent from TinyMCE
                    "padding-left": [/^\d+(?:px|em|%)$/],
                    // text-decoration from TinyMCE
                    "text-decoration": [/^underline$/, /^line-through$/]
                }
            },
            // Lots of these won"t come up by default because we don"t allow them
            selfClosing: ["img", "br", "hr", "area", "base", "basefont", "input", "link", "meta"],
            // URL schemes we permit
            allowedSchemes: ["http", "https", "ftp", "mailto"],
            allowedSchemesByTag: {},
            allowedSchemesAppliedToAttributes: ["href", "src", "cite"],
            allowProtocolRelative: true
        };
    }

    /** @returns default html tinyMce options for sanitize string */
    public static getStanitizerTinyMceOptions(): sanitize.IOptions {
        const options = StringMarshaller.getStanitizerHtmlStrictOptions();

        // Add <img> options for tinyMceHtmlOptions
        if (!options.allowedTags) {
            options.allowedTags = [];
        }
        options.allowedTags.push("img");

        if (!options.allowedAttributes) {
            options.allowedAttributes = {};
        }
        options.allowedAttributes["img"].push("width", "height", "alt", "title", "style");

        if (!options.allowedStyles) {
            options.allowedStyles = {};
        }
        options.allowedStyles["img"] = {
            "display": [/^block$/],
            "margin-left": [/^auto$/],
            "margin-right": [/^auto$/],
            "float": [/^left$/, /^right$/]
        };

        return options;
    }
}

/**
 * Add string marshallers to map
 */
export function setStringMarshallers<FieldTypeName>(marshallers: Map<FieldTypeName, FieldMarshaller | string>, stringFieldConstraints: Map<FieldTypeName, StringFieldConstraints>): void {
    for (const typeName of stringFieldConstraints.keys()) {
        marshallers.set(typeName, new StringMarshaller({ maxLength: stringFieldConstraints.get(typeName)?.maxLength }));
    }
}